import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/query-keys';
import { getMyCapabilities } from '@/api/endpoints/me';
import type { MyCapabilities } from '@/api/schemas/me';
import { useAuthStore } from './auth-store';

const CAPABILITIES_STALE_MS = 5 * 60 * 1000;
export const CAPABILITIES_RETRIES = 2;

/**
 * What the panel knows about ONE capability of the current operator.
 *
 * Three values and not two, because "no" and "we could not find out" lead to
 * different screens. `denied` hides a door the backend would answer 403 to;
 * `unknown` shows it and lets the backend be the boundary, which is what it is
 * anyway.
 */
export type CapabilityVerdict = 'allowed' | 'denied' | 'unknown';

/**
 * The source values `GET /admin/me/capabilities` reports, and what each one
 * means for gating:
 *
 *   - `ADMIN` — the caller is an ADMIN. Since ko-backend#842 the capability
 *     guard lets that role past without reading any snapshot, and the map comes
 *     back all `true`.
 *   - `STAFF_PROFILE` — the map reflects a profile somebody configured, so a
 *     `false` is a decision and can be trusted as a refusal.
 *   - `NO_STAFF_PROFILE` — a STAFF member with no profile. The backend refuses
 *     every capability route to them, so the all-`false` map is exactly right.
 *
 * `rootAdmin` is still in the shape for older builds and is always `false` now
 * that `PLATFORM_ROOT_ADMIN_IDS` opens nothing; it is honoured anyway, because
 * it can only ever have said "yes".
 */
function isAdminAnswer(data: MyCapabilities): boolean {
  return data.source === 'ADMIN' || data.role === 'ADMIN' || data.rootAdmin;
}

/**
 * THE DECISION, after ko-backend#842: an ADMIN may do everything, so an ADMIN
 * answer is `allowed` whatever the map says. That is also what used to make the
 * all-`false` map of an admin with no StaffProfile unsafe to gate on — and it is
 * gone, because the role alone decides for them now.
 *
 * `unknown` is left for what the panel really does not know: the answer has not
 * arrived or the request failed, or a key this build has never heard of.
 * Callers show on `unknown`; the backend is the boundary regardless.
 */
export function verdictOf(data: MyCapabilities | undefined, capability: string): CapabilityVerdict {
  if (!data) return 'unknown';
  if (isAdminAnswer(data)) return 'allowed';
  const value = data.capabilities[capability];
  // A key this build has never heard of is not a refusal either: the server
  // answers every capability IT knows, and a newer one is missing here rather
  // than false.
  if (value === undefined) return 'unknown';
  return value ? 'allowed' : 'denied';
}

/**
 * The caller's own permissions.
 *
 * Cached for the session rather than per screen: it is one row keyed by the
 * bearer, several sections gate on it, and a panel that re-asks "what may I do"
 * on every navigation adds a round trip to every navigation. A grant made while
 * the operator is logged in lands on the next reload — and, until then, on the
 * backend's own 403, which is the authority regardless.
 */
export function useMyCapabilities() {
  const who = useAuthStore((s) => s.user?.id ?? null);
  // A store owner (role USER, 2026-11-02) has no capabilities to ask for:
  // `/admin/me/capabilities` would answer 403 and raise the error banner.
  const isStoreOwner = useAuthStore((s) => s.user?.role === 'USER');
  return useQuery({ ...capabilitiesQuery(who), enabled: !isStoreOwner });
}

/**
 * The query both the router and the screens read, so they share one cache
 * entry. Keyed by who is signed in: the answer is about the bearer, and a tab
 * where one operator logs out and another logs in must not go on painting the
 * first one's sections for the next five minutes.
 */
export function capabilitiesQuery(who: string | null) {
  return {
    queryKey: queryKeys.me.capabilities(who),
    queryFn: getMyCapabilities,
    staleTime: CAPABILITIES_STALE_MS,
    // A failure here must not cascade into a panel that hides everything: the
    // verdict falls back to `unknown`, which shows the section.
    //
    // ko-admin#314 — but one dropped request is not worth a session of 403s
    // either: two quick retries first (200 ms, 400 ms), and only then the
    // failure, which `CapabilitiesErrorBanner` puts on screen.
    retry: CAPABILITIES_RETRIES,
    retryDelay: (attempt: number) => 200 * 2 ** attempt,
    // The router asks first (`ensureQueryData` in `beforeLoad`); without this,
    // every screen that mounts on the failed entry starts the three attempts
    // over again. A failure stays a failure until «Reintentar» or the next
    // stale refetch.
    retryOnMount: false,
  };
}

/** The verdict for one capability. `unknown` while the answer is in flight. */
export function useCapability(capability: string): CapabilityVerdict {
  return verdictOf(useMyCapabilities().data, capability);
}

/**
 * The verdict plus whether the question has been ANSWERED at all — success or
 * failure. A caller that gates a request wants to hold it back until then:
 * firing it while the answer is in flight is a round trip whose only possible
 * outcome, if the answer turns out to be no, is a 403.
 */
export function useCapabilityGate(capability: string): {
  verdict: CapabilityVerdict;
  isSettled: boolean;
} {
  const q = useMyCapabilities();
  return { verdict: verdictOf(q.data, capability), isSettled: q.isFetched };
}

/**
 * The gate for a route that takes ANY of several capabilities (ko-backend
 * `@RequireAnyStaffCapability`). Allowed when one is allowed, denied only when
 * every one is denied; anything else is `unknown`, which sends the request and
 * lets the backend decide.
 */
export function useAnyCapabilityGate(capabilities: readonly string[]): {
  verdict: CapabilityVerdict;
  isSettled: boolean;
} {
  const q = useMyCapabilities();
  const verdicts = capabilities.map((c) => verdictOf(q.data, c));
  const verdict: CapabilityVerdict = verdicts.includes('allowed')
    ? 'allowed'
    : verdicts.length > 0 && verdicts.every((v) => v === 'denied')
      ? 'denied'
      : 'unknown';
  return { verdict, isSettled: q.isFetched };
}

/**
 * The gate for an action whose backend route names NO capability — a bare
 * `@RequireRole('ADMIN')`, which a STAFF member never passes whatever their
 * profile holds (`RequireRoleGuard` only lets STAFF through where a capability
 * is named). There is nothing for `CapabilityAction` to ask, so the role
 * decides.
 *
 * Hidden only when the role is KNOWN not to be ADMIN. With no role at all the
 * panel fails open, as the menu does: the backend is the boundary, and an
 * admin whose session lost its claim should not lose the control too.
 */
export function useIsAdmin(): boolean {
  const role = useAuthStore((s) => s.user?.role ?? null);
  return role === null || role === 'ADMIN';
}
